import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  PrivacyRequestKind,
  PrivacyRequestStatus,
  type Customer,
  type PrivacyRequest,
} from '@nucleus-commerce/api-client/admin';

import { customerListQueryOptions, customerName, customerQueryOptions } from '@/features/customers';

/** Fields GET /privacy-requests sorts on; anything else is a 400. */
export const PRIVACY_REQUEST_SORTS = ['requestedAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const PRIVACY_REQUEST_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(PrivacyRequestStatus) },
  { key: 'kind', type: 'enum', options: valuesOf(PrivacyRequestKind) },
  { key: 'customerId', type: 'async' },
  { key: 'requestedAt', type: 'date-range' },
] as const;

const customerOption = (customer: Customer): FilterOption => ({
  value: customer.id,
  label: customerName(customer) ?? customer.email,
  description: customer.email,
});

export interface PrivacyRequestFiltersOptions {
  /** The `privacyRequests` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the privacy requests table, in the order the filter menu lists them. */
export function privacyRequestFilters({
  t,
  queryClient,
  storeId,
}: PrivacyRequestFiltersOptions): Array<FilterDef<PrivacyRequest>> {
  const options = (values: Record<string, string>, prefix: string): FilterOption[] =>
    valuesOf(values).map(({ value }) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(PrivacyRequestStatus, 'status'),
    },
    {
      key: 'kind',
      label: t('filters.kind'),
      type: 'enum',
      pinned: true,
      options: options(PrivacyRequestKind, 'kind'),
    },
    {
      key: 'customerId',
      label: t('filters.customer'),
      type: 'async',
      // The endpoint takes one customer.
      multiple: false,
      aliases: ['customer'],
      ...queryFilterOptions(queryClient, {
        search: (q) => customerListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(customerOption),
        resolve: {
          query: ([id]) => customerQueryOptions(storeId, id ?? ''),
          toOptions: (customer) => [customerOption(customer)],
        },
      }),
    },
    {
      key: 'requestedAt',
      label: t('filters.requestedAt'),
      type: 'date-range',
      aliases: ['requested'],
    },
  ];
}
